import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  Flex,
  Image,
  Link,
  Typography,
  Button,
  ExportIcon,
  EditIcon,
  Box,
} from '@iheartjane/reefer';
import type { CategoryImageKey, CategoryImagesConfig } from '@iheartjane/types';
import { titleCase } from '@iheartjane/utils';
import { Fragment, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { isCustomCategoryImage } from '../CategoryImage.util';
import { CategoryImageModal } from './CategoryImageModal';
import { FormSection } from './FormSection';

const CategoryImageDisplay = ({
  handleEdit,
  image,
  isCustom,
  label,
}: {
  handleEdit: () => void;
  image: string;
  isCustom: boolean;
  label: string;
}) => {
  const Icon = isCustom ? EditIcon : ExportIcon;
  return (
    <Flex justifyContent="space-between" width="100%">
      <Flex gap={8}>
        <Image
          altText={label}
          className="category_icon"
          height="48px"
          width="48px"
          key={image}
          src={image}
          objectFit="contain"
          borderRadius="8px"
        />
        <Flex flexDirection="column">
          <Typography variant="small-body-semibold">{label}</Typography>
          <Typography variant="small-body-regular">
            {isCustom ? 'Custom' : 'Default'}
          </Typography>
        </Flex>
      </Flex>

      <Button.Icon
        aria-label={`${isCustom ? 'Edit' : 'Upload'} ${label} image`}
        icon={<Icon color="primary" size="sm" />}
        onClick={handleEdit}
        size="small"
        variant="secondary"
        style={{
          '--btn-icon-small-height': '40px',
        }}
      />
    </Flex>
  );
};

export const CategoryTabsSection = () => {
  const customCategoryImages = useFlag(FLAGS.c2CustomCategoryImages);
  const [imagesExpanded, setImagesExpanded] = useState(false);
  const [selectedCategoryKey, setSelectedCategoryKey] =
    useState<CategoryImageKey | null>(null);
  const { watch } = useFormContext();
  const categoryTabsStyle = watch('category_tabs.style');
  const categoryImages: CategoryImagesConfig = watch('category_images');

  const categoryImagesToDisplay: CategoryImageKey[] = useMemo(
    () =>
      Object.keys(categoryImages ?? {})
        .map((key) => key as CategoryImageKey)
        .slice(0, imagesExpanded ? undefined : 3),
    [categoryImages, imagesExpanded]
  );

  return (
    <Flex gap={24} flexDirection="column">
      <Flex gap={16} flexDirection="column">
        <FormSection
          name="category_tabs.style"
          title="Style"
          subtitle="Visual treatement of each category card"
          options={[
            {
              label: 'Stacked',
              value: 'icon',
              defaultOption: true,
            },
            {
              label: 'Pills',
              value: 'pill',
            },
            {
              label: 'Tiles',
              value: 'tile',
            },
            {
              label: 'Tabs',
              value: 'tab',
            },
            {
              label: 'Image cards',
              value: 'image',
            },
          ]}
        />

        {customCategoryImages && categoryTabsStyle === 'image' && (
          <>
            <Typography variant="small-body-regular">
              Jane&apos;s defaults shown. Upload your own anytime.
            </Typography>

            <Flex alignItems="center" flexDirection="column" gap={12}>
              {categoryImagesToDisplay.map((key, index) => {
                const image = categoryImages[key];
                if (!image) return null;

                return (
                  <Fragment key={key}>
                    <CategoryImageDisplay
                      handleEdit={() => setSelectedCategoryKey(key)}
                      image={image}
                      key={key}
                      isCustom={isCustomCategoryImage(image)}
                      label={titleCase(key)}
                    />
                    {index < categoryImagesToDisplay.length - 1 && (
                      <Box background="grays-light" height="1px" width="100%" />
                    )}
                  </Fragment>
                );
              })}

              <Link
                onClick={() => setImagesExpanded(!imagesExpanded)}
                color="primary"
                typography="small-body-regular"
              >
                {imagesExpanded ? 'View less' : 'View all 10 categories'}
              </Link>
            </Flex>

            <CategoryImageModal
              open={!!selectedCategoryKey}
              onRequestClose={() => setSelectedCategoryKey(null)}
              categoryKey={selectedCategoryKey}
            />
          </>
        )}
      </Flex>

      <FormSection
        name="category_tabs.position"
        title="Placement"
        subtitle="Where the category row sits on the page"
        helperText="Only applies if your featured page has a carousel"
        options={[
          {
            label: 'Above carousel',
            value: 'above_carousel',
            defaultOption: true,
          },
          {
            label: 'Below carousel',
            value: 'below_carousel',
          },
        ]}
      />
    </Flex>
  );
};
